import React from "react";

/* eslint-disable react/no-unknown-property */

/**
 * A library of hand-built SVG diagrams for the nursing curriculum.
 * Static — no LLM cost. Bilingual labels (EN/AR) based on `lang` prop.
 * Each chapter gets a curated set rendered at the bottom of the lesson.
 */

const TXT = {
  maslowTitle:    { en: "Maslow's Hierarchy of Human Needs",       ar: "هرم ماسلو للاحتياجات الإنسانية" },
  maslow5:        { en: "Self-actualization", ar: "تحقيق الذات" },
  maslow4:        { en: "Esteem",             ar: "التقدير" },
  maslow3:        { en: "Love & belonging",   ar: "الحب والانتماء" },
  maslow2:        { en: "Safety & security",  ar: "الأمان والسلامة" },
  maslow1:        { en: "Physiological",      ar: "الاحتياجات الفسيولوجية" },

  commTitle:   { en: "The Communication Process", ar: "عملية التواصل" },
  sender:      { en: "Sender",   ar: "المرسِل" },
  message:     { en: "Message",  ar: "الرسالة" },
  channel:     { en: "Channel",  ar: "القناة" },
  receiver:    { en: "Receiver", ar: "المستقبِل" },
  feedback:    { en: "Feedback", ar: "التغذية الراجعة" },

  adpieTitle:  { en: "Nursing Process — ADPIE", ar: "عملية التمريض — ADPIE" },
  assess:      { en: "Assessment",  ar: "التقييم" },
  diag:        { en: "Diagnosis",   ar: "التشخيص" },
  plan:        { en: "Planning",    ar: "التخطيط" },
  impl:        { en: "Implementation", ar: "التنفيذ" },
  eval:        { en: "Evaluation",  ar: "التقويم" },

  fluidTitle:  { en: "Body Fluid Compartments (Adult, ~60% Body Weight)", ar: "حجرات السوائل في الجسم (البالغ، ~60% من وزن الجسم)" },
  icf:         { en: "Intracellular fluid (ICF) · 40%", ar: "السائل داخل الخلايا · 40%" },
  ecfPlasma:   { en: "Plasma · 5%",                     ar: "البلازما · 5%" },
  ecfIf:       { en: "Interstitial fluid · 15%",        ar: "السائل بين الخلايا · 15%" },

  admitTitle:  { en: "Admission → Transfer → Discharge Flow", ar: "تدفق: القبول ← التحويل ← الخروج" },
  admit:       { en: "Admission",  ar: "القبول" },
  transfer:    { en: "Transfer / Referral", ar: "التحويل / الإحالة" },
  discharge:   { en: "Discharge",  ar: "الخروج" },
  admitSteps:  { en: "Orient · Baseline vitals · ID band · Consents",  ar: "التهيئة · العلامات الحيوية الأساسية · سوار الهوية · الموافقات" },
  transferSteps:{ en: "Hand-off · SBAR · Secure records",              ar: "التسليم · SBAR · تأمين السجلات" },
  dischargeSteps:{ en: "Teaching · Meds reconciliation · Follow-up",   ar: "التثقيف · مراجعة الأدوية · المتابعة" },

  cprTitle:    { en: "Adult CPR — Compressions", ar: "الإنعاش القلبي الرئوي للبالغين — الضغطات" },
  cprRate:     { en: "Rate 100–120 / min", ar: "المعدل 100–120 / دقيقة" },
  cprDepth:    { en: "Depth 5–6 cm (2–2.4 in)", ar: "العمق 5–6 سم" },
  cprRatio:    { en: "30 compressions : 2 breaths", ar: "30 ضغطة : نفسان" },
  cprRecoil:   { en: "Full chest recoil between compressions", ar: "ارتداد كامل للصدر بين الضغطات" },

  // ———— Ch1 ————
  preventionTitle: { en: "Three Levels of Prevention", ar: "مستويات الوقاية الثلاثة" },
  primary:    { en: "Primary",   ar: "الوقاية الأولية" },
  secondary:  { en: "Secondary", ar: "الوقاية الثانوية" },
  tertiary:   { en: "Tertiary",  ar: "الوقاية الثلاثية" },
  primaryDesc:{ en: "Prevent disease before it occurs — vaccines, education, healthy lifestyle.", ar: "منع المرض قبل حدوثه — اللقاحات، التثقيف، نمط حياة صحي." },
  secondaryDesc:{ en: "Early detection & treatment — screenings, routine check-ups.", ar: "الاكتشاف المبكر والعلاج — الفحوصات الروتينية." },
  tertiaryDesc:{ en: "Minimise impact of ongoing disease — rehabilitation, chronic-care management.", ar: "تقليل أثر المرض المستمر — إعادة التأهيل، إدارة الأمراض المزمنة." },

  rolesTitle: { en: "Core Roles of the Nurse", ar: "أدوار الممرض/ة الأساسية" },
  roleCaregiver: { en: "Caregiver", ar: "مقدم الرعاية" },
  roleEducator:  { en: "Educator",  ar: "المعلم" },
  roleAdvocate:  { en: "Advocate",  ar: "المدافع" },
  roleManager:   { en: "Manager",   ar: "المدير" },
  roleResearcher:{ en: "Researcher",ar: "الباحث" },
  roleCommunicator:{ en: "Communicator", ar: "المتواصل" },
  roleCollaborator:{ en: "Collaborator", ar: "المتعاون" },
  roleCounselor: { en: "Counselor", ar: "المرشد" },

  // ———— Ch2 ————
  sbarTitle: { en: "SBAR Hand-off Template", ar: "نموذج التسليم SBAR" },
  sbarS: { en: "Situation",      ar: "الموقف" },
  sbarB: { en: "Background",     ar: "الخلفية" },
  sbarA: { en: "Assessment",     ar: "التقييم" },
  sbarR: { en: "Recommendation", ar: "التوصية" },
  sbarSd:{ en: "Who · where · why are you calling right now?", ar: "من؟ أين؟ لماذا تتصل الآن؟" },
  sbarBd:{ en: "Relevant history, current meds, code status.", ar: "التاريخ المرضي، الأدوية، حالة الإنعاش." },
  sbarAd:{ en: "Vital signs, findings, your clinical impression.", ar: "العلامات الحيوية، الموجودات، انطباعك السريري." },
  sbarRd:{ en: "What do you need? What action are you asking for?", ar: "ما الذي تحتاجه؟ ما الإجراء المطلوب؟" },

  therTitle: { en: "Therapeutic vs Non-Therapeutic Communication", ar: "التواصل العلاجي مقابل غير العلاجي" },
  therGood: { en: "Use active listening · open questions · silence · empathy · reflection", ar: "الإنصات الفعّال · الأسئلة المفتوحة · الصمت · التعاطف · التأمل" },
  therBad:  { en: "Avoid judging · false reassurance · changing subject · giving advice · 'why' questions", ar: "تجنّب: إصدار الأحكام · الطمأنة الزائفة · تغيير الموضوع · إعطاء النصيحة · أسئلة 'لماذا'" },

  // ———— Ch3 ————
  smartTitle: { en: "SMART Goals — Writing Patient Goals", ar: "أهداف SMART — كتابة أهداف المريض" },
  smartS: { en: "Specific",   ar: "محدد" },
  smartM: { en: "Measurable", ar: "قابل للقياس" },
  smartA: { en: "Achievable", ar: "قابل للتحقيق" },
  smartR: { en: "Realistic",  ar: "واقعي" },
  smartT: { en: "Time-bound", ar: "محدد بزمن" },
  smartEx:{ en: "Example: 'Patient will walk 50 m with walker within 3 days.'", ar: "مثال: 'سيمشي المريض 50م بالعكاز خلال 3 أيام.'" },

  subobjTitle: { en: "Subjective vs Objective Assessment Data", ar: "بيانات التقييم الذاتية مقابل الموضوعية" },
  subHead: { en: "Subjective", ar: "ذاتية" },
  objHead: { en: "Objective",  ar: "موضوعية" },
  subItems: { en: "What the patient SAYS · pain rating · 'I feel dizzy' · complaints · history", ar: "ما يقوله المريض · تقدير الألم · 'أشعر بالدوار' · الشكاوى · التاريخ" },
  objItems: { en: "What YOU observe · vital signs · lab values · skin colour · wound appearance", ar: "ما تلاحظه أنت · العلامات الحيوية · التحاليل · لون الجلد · مظهر الجرح" },

  // ———— Ch4 ————
  vitalsTitle: { en: "Adult Vital Signs — Normal Ranges", ar: "العلامات الحيوية للبالغين — المدى الطبيعي" },
  vTemp: { en: "Temperature · 36.5–37.5 °C", ar: "الحرارة · 36.5–37.5 °م" },
  vPulse:{ en: "Pulse · 60–100 bpm",         ar: "النبض · 60–100 نبضة/دقيقة" },
  vResp: { en: "Respirations · 12–20 / min", ar: "التنفس · 12–20 / دقيقة" },
  vBP:   { en: "BP · < 120 / 80 mmHg",       ar: "الضغط · أقل من 120 / 80 ملم زئبق" },
  vSpO2: { en: "SpO₂ · 95–100 %",            ar: "تشبع الأكسجين · 95–100 %" },

  painTitle: { en: "Wong-Baker FACES Pain Scale (0–10)", ar: "مقياس الألم الوجهي Wong-Baker (0–10)" },
  painNone: { en: "No pain",           ar: "لا ألم" },
  painMild: { en: "Hurts a little",    ar: "ألم خفيف" },
  painMod:  { en: "Hurts a bit more",  ar: "ألم متوسط" },
  painBad:  { en: "Hurts even more",   ar: "ألم شديد" },
  painVBad: { en: "Hurts a whole lot", ar: "ألم شديد جدًا" },
  painWorst:{ en: "Hurts worst",       ar: "أسوأ ألم ممكن" },

  // ———— Ch5 ————
  consentTitle: { en: "Types of Consent", ar: "أنواع الموافقة" },
  consentImplied:  { en: "Implied",  ar: "ضمنية" },
  consentExpress:  { en: "Expressed",ar: "صريحة" },
  consentInformed: { en: "Informed", ar: "واعية" },
  consentImpliedD: { en: "Actions show agreement — rolling up a sleeve for a BP cuff.", ar: "تصرف يدل على الموافقة — رفع الكم لقياس الضغط." },
  consentExpressD: { en: "Verbally or in writing — routine procedures.", ar: "شفهيًا أو كتابيًا — الإجراءات الروتينية." },
  consentInformedD:{ en: "Full explanation of risks, benefits, alternatives BEFORE signing.", ar: "شرح كامل للمخاطر والفوائد والبدائل قبل التوقيع." },

  // ———— Ch6 ————
  chainTitle: { en: "Chain of Survival (Adult)", ar: "سلسلة النجاة للبالغين" },
  chain1: { en: "Recognise & call for help", ar: "التعرّف وطلب المساعدة" },
  chain2: { en: "Early CPR",                 ar: "الإنعاش القلبي الرئوي المبكر" },
  chain3: { en: "Early defibrillation",      ar: "الصدمة الكهربائية المبكرة" },
  chain4: { en: "Advanced life support",     ar: "دعم الحياة المتقدم" },
  chain5: { en: "Post-arrest care",          ar: "الرعاية بعد السكتة" },

  fireTitle: { en: "Fire Safety — RACE & PASS", ar: "سلامة الحرائق — RACE و PASS" },
  raceR: { en: "Rescue",   ar: "أنقذ" },
  raceA: { en: "Alarm",    ar: "أنذر" },
  raceC: { en: "Contain",  ar: "احتوِ" },
  raceE: { en: "Extinguish / Evacuate", ar: "أخمد / أخلِ" },
  passP1:{ en: "Pull the pin",    ar: "اسحب الدبوس" },
  passA1:{ en: "Aim at the base", ar: "وجّه نحو القاعدة" },
  passS1:{ en: "Squeeze handle",  ar: "اضغط المقبض" },
  passS2:{ en: "Sweep side-to-side", ar: "اكنس يمنة ويسرى" },

  burnsTitle: { en: "Burn Degrees", ar: "درجات الحروق" },
  burn1: { en: "1° Superficial",       ar: "الدرجة الأولى — سطحية" },
  burn2: { en: "2° Partial thickness", ar: "الدرجة الثانية — جزئية" },
  burn3: { en: "3° Full thickness",    ar: "الدرجة الثالثة — كاملة" },
  burn1d:{ en: "Epidermis · red · painful · no blisters", ar: "البشرة · أحمر · مؤلم · بدون فقاعات" },
  burn2d:{ en: "Epidermis + dermis · blisters · very painful", ar: "البشرة + الأدمة · فقاعات · شديد الألم" },
  burn3d:{ en: "All layers · white/charred · painless (nerves destroyed)", ar: "جميع الطبقات · أبيض/متفحّم · بلا ألم (تدمّر الأعصاب)" },
};

function T(key, lang) {
  return (TXT[key] || {})[lang] || (TXT[key] || {}).en || key;
}

/* ======================== Individual diagrams ======================== */

function MaslowPyramid({ lang }) {
  const levels = [
    { key: "maslow5", y: 16,  w: 160, fill: "#4A675C" },
    { key: "maslow4", y: 70,  w: 220, fill: "#5a7a6e" },
    { key: "maslow3", y: 124, w: 280, fill: "#6E8D81" },
    { key: "maslow2", y: 178, w: 340, fill: "#D97757" },
    { key: "maslow1", y: 232, w: 400, fill: "#C26544" },
  ];
  return (
    <DiagramCard title={T("maslowTitle", lang)}>
      <svg viewBox="0 0 560 300" className="w-full h-auto" role="img">
        {levels.map((l, i) => (
          <g key={l.key}>
            <polygon
              points={`${280 - l.w / 2},${l.y + 48} ${280 + l.w / 2},${l.y + 48} ${280 + (l.w - 80) / 2},${l.y} ${280 - (l.w - 80) / 2},${l.y}`}
              fill={l.fill}
              opacity={0.92}
            />
            <text
              x={280}
              y={l.y + 30}
              textAnchor="middle"
              className="fill-white"
              fontSize={i === 0 ? 11 : i === 1 ? 13 : 15}
              fontWeight={600}
            >
              {T(l.key, lang)}
            </text>
          </g>
        ))}
      </svg>
    </DiagramCard>
  );
}

function CommunicationLoop({ lang }) {
  return (
    <DiagramCard title={T("commTitle", lang)}>
      <svg viewBox="0 0 640 220" className="w-full h-auto" role="img">
        <defs>
          <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" fill="#4A675C" />
          </marker>
        </defs>
        {/* Sender */}
        <Pill x={40} y={70} w={140} h={70} fill="#4A675C" text={T("sender", lang)} fg="#fff" />
        {/* Channel in the middle */}
        <Pill x={250} y={70} w={140} h={70} fill="#fff" stroke="#4A675C" text={T("channel", lang)} fg="#2e3b36" />
        {/* Receiver */}
        <Pill x={460} y={70} w={140} h={70} fill="#D97757" text={T("receiver", lang)} fg="#fff" />
        {/* Arrows forward */}
        <path d="M182 95 L248 95" stroke="#4A675C" strokeWidth={2.5} markerEnd="url(#arr)" fill="none" />
        <path d="M392 95 L458 95" stroke="#4A675C" strokeWidth={2.5} markerEnd="url(#arr)" fill="none" />
        <text x={215} y={85} fontSize="11" fill="#6E7A75" textAnchor="middle">{T("message", lang)}</text>
        {/* Feedback arc */}
        <path d="M530 140 Q 320 210 110 140" stroke="#D97757" strokeWidth={2.2} strokeDasharray="5 5" fill="none" markerEnd="url(#arr)" />
        <text x={320} y={200} fontSize="12" fill="#D97757" textAnchor="middle" fontWeight={600}>{T("feedback", lang)}</text>
      </svg>
    </DiagramCard>
  );
}

function ADPIEWheel({ lang }) {
  const items = [
    { key: "assess", angle: -90, color: "#4A675C" },
    { key: "diag",   angle: -18, color: "#5a7a6e" },
    { key: "plan",   angle:  54, color: "#6E8D81" },
    { key: "impl",   angle: 126, color: "#D97757" },
    { key: "eval",   angle: 198, color: "#C26544" },
  ];
  const cx = 200, cy = 180, r = 120;
  return (
    <DiagramCard title={T("adpieTitle", lang)}>
      <svg viewBox="0 0 400 360" className="w-full h-auto" role="img">
        <circle cx={cx} cy={cy} r={r - 40} fill="#fdfaf4" stroke="#e5d9c9" strokeWidth={2} />
        {items.map((item, i) => {
          const rad = (item.angle * Math.PI) / 180;
          const x = cx + r * Math.cos(rad);
          const y = cy + r * Math.sin(rad);
          return (
            <g key={item.key}>
              <circle cx={x} cy={y} r={40} fill={item.color} />
              <text x={x} y={y + 5} textAnchor="middle" fill="#fff" fontSize={13} fontWeight={600}>
                {i + 1}
              </text>
              <text x={x} y={y + 60} textAnchor="middle" fill="#2e3b36" fontSize={13} fontWeight={500}>
                {T(item.key, lang)}
              </text>
            </g>
          );
        })}
        {/* Center label */}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize={13} fill="#6E7A75">Nursing</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize={13} fontWeight={700} fill="#4A675C">Process</text>
      </svg>
    </DiagramCard>
  );
}

function FluidCompartments({ lang }) {
  return (
    <DiagramCard title={T("fluidTitle", lang)}>
      <svg viewBox="0 0 640 220" className="w-full h-auto" role="img">
        {/* ICF 40% = width 400 */}
        <rect x={20} y={40} width={400} height={140} rx={14} fill="#4A675C" />
        <text x={220} y={110} textAnchor="middle" fill="#fff" fontSize={18} fontWeight={600}>ICF · 40%</text>
        <text x={220} y={140} textAnchor="middle" fill="#e5f0ea" fontSize={12}>{T("icf", lang)}</text>
        {/* Interstitial 15% = width 150 */}
        <rect x={430} y={40} width={150} height={140} rx={14} fill="#D97757" />
        <text x={505} y={105} textAnchor="middle" fill="#fff" fontSize={16} fontWeight={600}>IF · 15%</text>
        <text x={505} y={130} textAnchor="middle" fill="#fff1e7" fontSize={11}>{T("ecfIf", lang)}</text>
        {/* Plasma 5% = width 50 */}
        <rect x={590} y={40} width={50} height={140} rx={10} fill="#B34A3A" />
        <text x={615} y={105} textAnchor="middle" fill="#fff" fontSize={13} fontWeight={700}>5%</text>
        <text x={615} y={130} textAnchor="middle" fill="#fff1e7" fontSize={10}>plasma</text>
      </svg>
    </DiagramCard>
  );
}

function AdmissionFlow({ lang }) {
  return (
    <DiagramCard title={T("admitTitle", lang)}>
      <svg viewBox="0 0 760 170" className="w-full h-auto" role="img">
        <defs>
          <marker id="arrA" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" fill="#4A675C" />
          </marker>
        </defs>
        <StepCard x={10}  y={30} w={220} h={100} color="#4A675C" title={T("admit", lang)}    sub={T("admitSteps", lang)} />
        <StepCard x={270} y={30} w={220} h={100} color="#5a7a6e" title={T("transfer", lang)} sub={T("transferSteps", lang)} />
        <StepCard x={530} y={30} w={220} h={100} color="#D97757" title={T("discharge", lang)}sub={T("dischargeSteps", lang)} />
        <path d="M230 80 L268 80" stroke="#4A675C" strokeWidth={2.5} fill="none" markerEnd="url(#arrA)" />
        <path d="M490 80 L528 80" stroke="#4A675C" strokeWidth={2.5} fill="none" markerEnd="url(#arrA)" />
      </svg>
    </DiagramCard>
  );
}

function CPRDiagram({ lang }) {
  return (
    <DiagramCard title={T("cprTitle", lang)}>
      <svg viewBox="0 0 640 260" className="w-full h-auto" role="img">
        {/* Torso outline */}
        <ellipse cx={200} cy={130} rx={120} ry={90} fill="#f5ebd8" stroke="#C26544" strokeWidth={2} />
        <circle cx={200} cy={40} r={25} fill="#f5ebd8" stroke="#C26544" strokeWidth={2} />
        {/* Hands */}
        <rect x={175} y={90} width={50} height={28} rx={8} fill="#D97757" />
        <rect x={175} y={115} width={50} height={28} rx={8} fill="#4A675C" />
        {/* Compression arrows */}
        <path d="M200 60 L200 90" stroke="#B34A3A" strokeWidth={3} markerEnd="url(#downArr)" />
        <defs>
          <marker id="downArr" viewBox="0 0 10 10" refX="5" refY="9" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0,0 L10,0 L5,10 z" fill="#B34A3A" />
          </marker>
        </defs>
        {/* Labels */}
        <g transform="translate(360 40)">
          <StatBox y={0}   color="#4A675C" label={T("cprRate", lang)} />
          <StatBox y={60}  color="#D97757" label={T("cprDepth", lang)} />
          <StatBox y={120} color="#5a7a6e" label={T("cprRatio", lang)} />
          <StatBox y={180} color="#C26544" label={T("cprRecoil", lang)} />
        </g>
      </svg>
    </DiagramCard>
  );
}

/* ======================== Ch 1 ======================== */

function LevelsOfPrevention({ lang }) {
  const items = [
    { key: "primary",   desc: "primaryDesc",   color: "#4A675C" },
    { key: "secondary", desc: "secondaryDesc", color: "#D97757" },
    { key: "tertiary",  desc: "tertiaryDesc",  color: "#C26544" },
  ];
  return (
    <DiagramCard title={T("preventionTitle", lang)}>
      <svg viewBox="0 0 640 220" className="w-full h-auto" role="img">
        {items.map((it, i) => (
          <g key={it.key} transform={`translate(${i * 210 + 10} 20)`}>
            <rect width="200" height="180" rx="14" fill={it.color} />
            <text x={100} y={40} fill="#fff" fontSize={20} fontWeight={700} textAnchor="middle">
              {i + 1}
            </text>
            <text x={100} y={72} fill="#fff" fontSize={18} fontWeight={700} textAnchor="middle">
              {T(it.key, lang)}
            </text>
            <foreignObject x={15} y={82} width={170} height={90}>
              <div xmlns="http://www.w3.org/1999/xhtml"
                   style={{ color: "#fff1e7", fontSize: 11, lineHeight: 1.35, textAlign: "center" }}>
                {T(it.desc, lang)}
              </div>
            </foreignObject>
          </g>
        ))}
      </svg>
    </DiagramCard>
  );
}

function NurseRolesWheel({ lang }) {
  const roles = ["roleCaregiver", "roleCommunicator", "roleEducator", "roleAdvocate",
                 "roleCollaborator", "roleManager", "roleCounselor", "roleResearcher"];
  const cx = 260, cy = 210, r = 150;
  return (
    <DiagramCard title={T("rolesTitle", lang)}>
      <svg viewBox="0 0 520 420" className="w-full h-auto" role="img">
        <circle cx={cx} cy={cy} r={70} fill="#fdfaf4" stroke="#4A675C" strokeWidth={2} />
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize={13} fill="#6E7A75">The</text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize={16} fontWeight={700} fill="#4A675C">Nurse</text>
        {roles.map((key, i) => {
          const angle = (i / roles.length) * 2 * Math.PI - Math.PI / 2;
          const x = cx + r * Math.cos(angle);
          const y = cy + r * Math.sin(angle);
          const color = i % 2 === 0 ? "#4A675C" : "#D97757";
          return (
            <g key={key}>
              <line x1={cx + 72 * Math.cos(angle)} y1={cy + 72 * Math.sin(angle)}
                    x2={x - 30 * Math.cos(angle)} y2={y - 30 * Math.sin(angle)}
                    stroke="#e5d9c9" strokeWidth={1.5} strokeDasharray="3 4" />
              <circle cx={x} cy={y} r={40} fill={color} />
              <text x={x} y={y + 5} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={600}>
                {T(key, lang).split(" ")[0]}
              </text>
            </g>
          );
        })}
      </svg>
    </DiagramCard>
  );
}

/* ======================== Ch 2 ======================== */

function SbarTemplate({ lang }) {
  const rows = [
    { l: "sbarS", d: "sbarSd", c: "#4A675C" },
    { l: "sbarB", d: "sbarBd", c: "#5a7a6e" },
    { l: "sbarA", d: "sbarAd", c: "#D97757" },
    { l: "sbarR", d: "sbarRd", c: "#C26544" },
  ];
  return (
    <DiagramCard title={T("sbarTitle", lang)}>
      <div className="grid grid-cols-[auto_1fr] gap-2">
        {rows.map((r) => (
          <React.Fragment key={r.l}>
            <div className="flex items-center justify-center w-16 rounded-xl font-display text-2xl font-bold text-white"
                 style={{ background: r.c }}>
              {T(r.l, lang).charAt(0)}
            </div>
            <div className="rounded-xl border border-border bg-white p-3">
              <div className="font-semibold text-sm mb-0.5">{T(r.l, lang)}</div>
              <div className="text-xs text-muted-foreground leading-relaxed">{T(r.d, lang)}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </DiagramCard>
  );
}

function TherapeuticCommTable({ lang }) {
  return (
    <DiagramCard title={T("therTitle", lang)}>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-sage/40 bg-sage/5 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-2">✓ Therapeutic</div>
          <div className="text-sm leading-relaxed">{T("therGood", lang)}</div>
        </div>
        <div className="rounded-2xl border border-terracotta/40 bg-terracotta/5 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta font-bold mb-2">✗ Avoid</div>
          <div className="text-sm leading-relaxed">{T("therBad", lang)}</div>
        </div>
      </div>
    </DiagramCard>
  );
}

/* ======================== Ch 3 ======================== */

function SmartGoals({ lang }) {
  const items = [
    { l: "smartS", color: "#4A675C" },
    { l: "smartM", color: "#5a7a6e" },
    { l: "smartA", color: "#6E8D81" },
    { l: "smartR", color: "#D97757" },
    { l: "smartT", color: "#C26544" },
  ];
  return (
    <DiagramCard title={T("smartTitle", lang)}>
      <div className="grid grid-cols-5 gap-2 mb-3">
        {items.map((it) => (
          <div key={it.l} className="rounded-xl p-3 text-center" style={{ background: it.color }}>
            <div className="font-display text-2xl font-bold text-white">{T(it.l, lang).charAt(0)}</div>
            <div className="text-[11px] font-medium text-white/90 mt-0.5">{T(it.l, lang)}</div>
          </div>
        ))}
      </div>
      <div className="bg-bone border border-border rounded-xl p-3 text-center text-xs text-muted-foreground italic">
        {T("smartEx", lang)}
      </div>
    </DiagramCard>
  );
}

function SubjectiveObjective({ lang }) {
  return (
    <DiagramCard title={T("subobjTitle", lang)}>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-2xl bg-sage p-4 text-white">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80 mb-2">👄 {T("subHead", lang)}</div>
          <div className="text-sm leading-relaxed">{T("subItems", lang)}</div>
        </div>
        <div className="rounded-2xl bg-terracotta p-4 text-white">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80 mb-2">👁 {T("objHead", lang)}</div>
          <div className="text-sm leading-relaxed">{T("objItems", lang)}</div>
        </div>
      </div>
    </DiagramCard>
  );
}

/* ======================== Ch 4 ======================== */

function VitalSignsRanges({ lang }) {
  const vitals = [
    { l: "vTemp",  color: "#D97757" },
    { l: "vPulse", color: "#B34A3A" },
    { l: "vResp",  color: "#4A675C" },
    { l: "vBP",    color: "#6E8D81" },
    { l: "vSpO2",  color: "#5a7a6e" },
  ];
  return (
    <DiagramCard title={T("vitalsTitle", lang)}>
      <div className="grid sm:grid-cols-5 gap-2">
        {vitals.map((v) => (
          <div key={v.l} className="rounded-xl p-3 text-center text-white" style={{ background: v.color }}>
            <div className="text-xs leading-tight font-medium">{T(v.l, lang)}</div>
          </div>
        ))}
      </div>
    </DiagramCard>
  );
}

function PainFaces({ lang }) {
  const faces = [
    { score: 0,  label: "painNone",  mouth: [18, 32, 0, 3],   fill: "#8fbfa3" },
    { score: 2,  label: "painMild",  mouth: [18, 32, 0, 1],   fill: "#b5cf85" },
    { score: 4,  label: "painMod",   mouth: [18, 32, 0, -2],  fill: "#e3cf6f" },
    { score: 6,  label: "painBad",   mouth: [18, 32, 0, -6],  fill: "#e8a35a" },
    { score: 8,  label: "painVBad",  mouth: [18, 32, 0, -10], fill: "#d97757" },
    { score: 10, label: "painWorst", mouth: [18, 32, 0, -14], fill: "#b34a3a" },
  ];
  return (
    <DiagramCard title={T("painTitle", lang)}>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {faces.map((f) => (
          <div key={f.score} className="flex flex-col items-center">
            <svg viewBox="0 0 60 60" width={64} height={64}>
              <circle cx={30} cy={30} r={26} fill={f.fill} stroke="#2e3b36" strokeWidth={1.5} />
              <circle cx={22} cy={25} r={2.5} fill="#2e3b36" />
              <circle cx={38} cy={25} r={2.5} fill="#2e3b36" />
              <path d={`M ${30 - f.mouth[0]/2} ${f.mouth[1]} Q 30 ${f.mouth[1] + f.mouth[3] * 2} ${30 + f.mouth[0]/2} ${f.mouth[1]}`}
                    stroke="#2e3b36" strokeWidth={2} fill="none" strokeLinecap="round" />
            </svg>
            <div className="mt-1 font-bold text-sm text-sage">{f.score}</div>
            <div className="text-[10px] text-muted-foreground text-center leading-tight">{T(f.label, lang)}</div>
          </div>
        ))}
      </div>
    </DiagramCard>
  );
}

/* ======================== Ch 5 ======================== */

function ConsentTypes({ lang }) {
  const items = [
    { l: "consentImplied",  d: "consentImpliedD",  color: "#6E8D81" },
    { l: "consentExpress",  d: "consentExpressD",  color: "#D97757" },
    { l: "consentInformed", d: "consentInformedD", color: "#4A675C" },
  ];
  return (
    <DiagramCard title={T("consentTitle", lang)}>
      <div className="grid sm:grid-cols-3 gap-3">
        {items.map((it) => (
          <div key={it.l} className="rounded-2xl p-4 text-white" style={{ background: it.color }}>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80 mb-1">{T(it.l, lang)}</div>
            <div className="text-xs leading-relaxed">{T(it.d, lang)}</div>
          </div>
        ))}
      </div>
    </DiagramCard>
  );
}

/* ======================== Ch 6 ======================== */

function ChainOfSurvival({ lang }) {
  const links = ["chain1", "chain2", "chain3", "chain4", "chain5"];
  return (
    <DiagramCard title={T("chainTitle", lang)}>
      <svg viewBox="0 0 760 140" className="w-full h-auto" role="img">
        {links.map((k, i) => {
          const x = i * 150 + 20;
          return (
            <g key={k}>
              <circle cx={x + 55} cy={70} r={40} fill={i % 2 ? "#D97757" : "#4A675C"} />
              <text x={x + 55} y={75} fill="#fff" fontSize={22} fontWeight={800} textAnchor="middle">{i + 1}</text>
              <foreignObject x={x} y={115} width={110} height={30}>
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontSize: 11, color: "#2e3b36", textAlign: "center", lineHeight: 1.2 }}>
                  {T(k, lang)}
                </div>
              </foreignObject>
              {i < links.length - 1 && (
                <line x1={x + 100} y1={70} x2={x + 155} y2={70} stroke="#C26544" strokeWidth={3} strokeLinecap="round" />
              )}
            </g>
          );
        })}
      </svg>
    </DiagramCard>
  );
}

function FireSafety({ lang }) {
  const race = [
    { k: "raceR", c: "#B34A3A" },
    { k: "raceA", c: "#D97757" },
    { k: "raceC", c: "#C26544" },
    { k: "raceE", c: "#4A675C" },
  ];
  const pass = [
    { k: "passP1", c: "#D97757" },
    { k: "passA1", c: "#6E8D81" },
    { k: "passS1", c: "#4A675C" },
    { k: "passS2", c: "#5a7a6e" },
  ];
  return (
    <DiagramCard title={T("fireTitle", lang)}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta font-bold mb-2">RACE · During fire</div>
          <div className="grid grid-cols-4 gap-1.5">
            {race.map((r) => (
              <div key={r.k} className="rounded-lg p-2 text-center text-white" style={{ background: r.c }}>
                <div className="font-display text-xl font-bold">{T(r.k, lang).charAt(0)}</div>
                <div className="text-[10px] font-medium">{T(r.k, lang)}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-2">PASS · Using extinguisher</div>
          <div className="grid grid-cols-4 gap-1.5">
            {pass.map((r) => (
              <div key={r.k} className="rounded-lg p-2 text-center text-white" style={{ background: r.c }}>
                <div className="font-display text-xl font-bold">{T(r.k, lang).charAt(0)}</div>
                <div className="text-[10px] font-medium">{T(r.k, lang).split(" ")[0]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DiagramCard>
  );
}

function BurnDegrees({ lang }) {
  return (
    <DiagramCard title={T("burnsTitle", lang)}>
      <svg viewBox="0 0 640 220" className="w-full h-auto" role="img">
        {/* Skin layers */}
        <rect x={20}  y={30} width={180} height={60} fill="#fde8d9" stroke="#C26544" strokeWidth={1.5} />
        <rect x={20}  y={90} width={180} height={60} fill="#f5cba8" stroke="#C26544" strokeWidth={1.5} />
        <rect x={20} y={150} width={180} height={40} fill="#e5b888" stroke="#C26544" strokeWidth={1.5} />
        <text x={205} y={64}  fill="#2e3b36" fontSize={11}>Epidermis</text>
        <text x={205} y={124} fill="#2e3b36" fontSize={11}>Dermis</text>
        <text x={205} y={174} fill="#2e3b36" fontSize={11}>Subcutaneous</text>
        {/* Burn zones */}
        <rect x={320} y={30} width={100} height={20} fill="#e88860" rx={3} />
        <rect x={320} y={30} width={100} height={55} fill="#d97757" opacity={0.7} rx={3} />
        <rect x={320} y={30} width={100} height={120} fill="#b34a3a" opacity={0.8} rx={3} />
        {/* Legend */}
        <g transform="translate(440 30)">
          <rect width={180} height={50} rx={8} fill="#4A675C" />
          <text x={10} y={22} fontSize={13} fontWeight={700} fill="#fff">{T("burn1", lang)}</text>
          <foreignObject x={10} y={26} width={165} height={24}>
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontSize: 10, color: "#e5f0ea", lineHeight: 1.25 }}>
              {T("burn1d", lang)}
            </div>
          </foreignObject>
        </g>
        <g transform="translate(440 85)">
          <rect width={180} height={50} rx={8} fill="#D97757" />
          <text x={10} y={22} fontSize={13} fontWeight={700} fill="#fff">{T("burn2", lang)}</text>
          <foreignObject x={10} y={26} width={165} height={24}>
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontSize: 10, color: "#fff1e7", lineHeight: 1.25 }}>
              {T("burn2d", lang)}
            </div>
          </foreignObject>
        </g>
        <g transform="translate(440 140)">
          <rect width={180} height={55} rx={8} fill="#B34A3A" />
          <text x={10} y={22} fontSize={13} fontWeight={700} fill="#fff">{T("burn3", lang)}</text>
          <foreignObject x={10} y={26} width={165} height={28}>
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontSize: 10, color: "#fff1e7", lineHeight: 1.25 }}>
              {T("burn3d", lang)}
            </div>
          </foreignObject>
        </g>
      </svg>
    </DiagramCard>
  );
}

/* ======================== Helpers ======================== */

function DiagramCard({ title, children }) {
  return (
    <div
      className="bg-white rounded-2xl border border-border p-5 sm:p-6"
      data-testid="diagram-card"
    >
      <div className="text-xs uppercase tracking-[0.2em] text-sage font-semibold mb-4">{title}</div>
      <div className="w-full max-w-3xl mx-auto">{children}</div>
    </div>
  );
}

function Pill({ x, y, w, h, fill, text, fg, stroke }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={h / 2}
            fill={fill}
            stroke={stroke || "none"}
            strokeWidth={stroke ? 2 : 0} />
      <text x={x + w / 2} y={y + h / 2 + 5} textAnchor="middle" fill={fg} fontSize={16} fontWeight={600}>
        {text}
      </text>
    </g>
  );
}

function StepCard({ x, y, w, h, color, title, sub }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={14} fill={color} />
      <text x={x + w / 2} y={y + 34} textAnchor="middle" fill="#fff" fontSize={17} fontWeight={700}>{title}</text>
      <foreignObject x={x + 10} y={y + 44} width={w - 20} height={h - 50}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{ color: "#fff1e7", fontSize: 11, lineHeight: 1.35, textAlign: "center" }}
        >
          {sub}
        </div>
      </foreignObject>
    </g>
  );
}

function StatBox({ y, color, label }) {
  return (
    <g transform={`translate(0 ${y})`}>
      <rect x={0} y={0} width={260} height={48} rx={10} fill={color} />
      <text x={20} y={30} fill="#fff" fontSize={15} fontWeight={600}>{label}</text>
    </g>
  );
}

/* ======================== Public mapping ======================== */

const REGISTRY = {
  maslow: MaslowPyramid,
  communication: CommunicationLoop,
  "nursing-process": ADPIEWheel,
  fluid: FluidCompartments,
  admission: AdmissionFlow,
  cpr: CPRDiagram,
  // Ch1
  prevention: LevelsOfPrevention,
  "nurse-roles": NurseRolesWheel,
  // Ch2
  sbar: SbarTemplate,
  therapeutic: TherapeuticCommTable,
  // Ch3
  smart: SmartGoals,
  "sub-obj": SubjectiveObjective,
  // Ch4
  vitals: VitalSignsRanges,
  "pain-faces": PainFaces,
  // Ch5
  consent: ConsentTypes,
  // Ch6
  "chain-of-survival": ChainOfSurvival,
  "fire-safety": FireSafety,
  burns: BurnDegrees,
};

// Which diagrams to show per chapter id
const CHAPTER_MAP = {
  ch1: ["prevention", "nurse-roles"],
  ch2: ["communication", "sbar", "therapeutic"],
  ch3: ["nursing-process", "smart", "sub-obj"],
  ch4: ["maslow", "vitals", "fluid", "pain-faces"],
  ch5: ["admission", "consent"],
  ch6: ["cpr", "chain-of-survival", "fire-safety", "burns"],
};

export default function ChapterDiagrams({ chapterId, lang = "en" }) {
  const slugs = CHAPTER_MAP[chapterId] || [];
  if (!slugs.length) return null;
  return (
    <div className="space-y-5 mt-8" data-testid="chapter-diagrams">
      {slugs.map((slug) => {
        const Cmp = REGISTRY[slug];
        if (!Cmp) return null;
        return <Cmp key={slug} lang={lang} />;
      })}
    </div>
  );
}
